import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, isEmpty, Text, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Form, FormRenderProps, RichTextField, RichtextReference, SubmitButton, TextField } from 'components/form'
import { CiapSelectField } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import EspecialidadeSisregSelectField from 'components/form/field/select/EspecialidadeSisregSelectField/EspecialidadeSisregSelectField'
import { confirm } from 'components/modals/confirm'
import { useFlags } from 'config/useFlagsContext'
import { FormApi } from 'final-form'
import { ClassificacaoRiscoEncaminhamentoEnum } from 'graphql/types.generated'
import { useMemo, useRef } from 'react'
import { metaPath } from 'util/metaPath'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { canRegisterCid10 } from '../cuidado-compartilhado/cuidado-compartilhado-form/utils-cuidadoCompartilhado'
import { ClassificacaoRiscoEncaminhamentoGroupButton } from './ClassificacaoRiscoEncaminhamentoGroupButton'
import {
  EncaminhamentoExternoEspecializadoModel,
  EncaminhamentoExternoEspecializadoPlanoModel,
} from './model-encaminhamentoExterno'
import { encaminhamentoExternoFormValidator } from './util/validator-encaminhamentoExternoForm'

export const path = metaPath<EncaminhamentoExternoEspecializadoModel>()

export interface EncaminhamentoExternoFormProps {
  prontuarioId: ID
  isAtendimentoOdonto: boolean
  cidadao: CidadaoAtendimento
  renderCiap: boolean
  onSubmit(values: EncaminhamentoExternoEspecializadoModel, formApi: FormApi): void
  encaminhamentosCache: EncaminhamentoExternoEspecializadoPlanoModel
}

const removeNonBreakingSpace = (value: string) => value.replace(/&nbsp;/g, '')

export default function EncaminhamentoExternoEspecializadoForm(props: Readonly<EncaminhamentoExternoFormProps>) {
  const { onSubmit, isAtendimentoOdonto, cidadao, renderCiap, encaminhamentosCache } = props
  const flags = useFlags()
  const {
    acesso: {
      cbo: { cbo2002 },
    },
  } = useAcessoLotacaoOrEstagio()

  const canAcessoCBORegisterCid10 = canRegisterCid10(cbo2002, renderCiap, flags)

  const alert = useAlert()
  const motivoRichTextFieldRef = useRef<RichtextReference>(null)
  const observacoesRichTextFieldRef = useRef<RichtextReference>(null)

  const handleSubmit = (values: EncaminhamentoExternoEspecializadoModel, formApi: FormApi) => {
    onSubmit(values, formApi)
    setTimeout(() => formApi.reset())
    formApi.getRegisteredFields().forEach((item) => formApi.resetFieldState(item))
    motivoRichTextFieldRef.current?.handleReset()
    observacoesRichTextFieldRef.current?.handleReset()
    alert('success', 'Encaminhamento salvo com sucesso')
  }

  const handleCancel = (formProps: FormRenderProps<EncaminhamentoExternoEspecializadoModel>) => {
    if (formProps.dirty) {
      confirm({
        title: 'Deseja sair sem salvar?',
        body: 'As alterações realizadas serão perdidas.',
        cancelLabel: 'Não, continuar aqui',
        confirmLabel: 'Sim, sair da tela',
        onConfirm: () => {
          alert('success', 'Encaminhamento cancelado com sucesso')
          setTimeout(() => formProps.form.reset())
          motivoRichTextFieldRef.current?.handleReset()
          observacoesRichTextFieldRef.current?.handleReset()
        },
      })()
    } else {
      alert('success', 'Encaminhamento cancelado com sucesso')
    }

    formProps.form.getRegisteredFields().forEach((item) => formProps.form.resetFieldState(item))
  }

  const renderForm = (formProps: FormRenderProps<EncaminhamentoExternoEspecializadoModel>) => {
    const sexoCidadao = isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : null

    return (
      <VFlow style={classes.container}>
        {formProps.error && <Text color='danger'>{formProps.error}</Text>}

        <Grid style={classes.formBody}>
          <Cell
            size={5}
            style={css`
              padding-top: 0;
            `}
          >
            <EspecialidadeSisregSelectField
              name={path.especialidadeSisreg}
              label='Especialidade'
              isAtendimentoOdonto={isAtendimentoOdonto}
              required
            />
          </Cell>

          <Cell
            size={5}
            style={css`
              padding-top: 0;
            `}
          >
            <TextField name={path.complemento} label='Complemento' maxLength={100} parse={removeNonBreakingSpace} />
          </Cell>

          <Cell size={5}>
            {canAcessoCBORegisterCid10 ? (
              <CidSelectField
                name={path.cidEncaminhamento}
                label='Hipótese/Diagnóstico (CID10)'
                renderGrupoCiapRelacionada={false}
                sexo={sexoCidadao}
                required
              />
            ) : (
              <CiapSelectField
                name={path.ciapEncaminhamento}
                label='Hipótese/Diagnóstico (CIAP2)'
                ciapComCid10Encaminhamento
                sexo={sexoCidadao}
                required
                fetchCID10Encaminhamento
              />
            )}
          </Cell>

          <Cell size={12}>
            <ClassificacaoRiscoEncaminhamentoGroupButton name={path.classificacaoRiscoEncaminhamento} />
          </Cell>

          <Cell size={12}>
            <RichTextField
              name={path.motivoEncaminhamento}
              ref={motivoRichTextFieldRef}
              label='Motivo do encaminhamento'
              maxLength={1000}
              removeNonBreakingSpaces
              placeholder='Insira o motivo do encaminhamento'
            />
          </Cell>

          <Cell size={12}>
            <RichTextField
              name={path.observacoes}
              ref={observacoesRichTextFieldRef}
              label='Observações'
              maxLength={300}
              removeNonBreakingSpaces
              placeholder='Insira outras observações'
            />
          </Cell>
        </Grid>

        <HFlow justifyContent='flex-end'>
          <Button size='small' onClick={() => handleCancel(formProps)}>
            Cancelar
          </Button>

          <SubmitButton size='small' kind='primary' handleSubmit={formProps.handleSubmit}>
            Adicionar
          </SubmitButton>
        </HFlow>
      </VFlow>
    )
  }

  const validator = useMemo(
    () =>
      encaminhamentoExternoFormValidator(
        canAcessoCBORegisterCid10,
        encaminhamentosCache?.encaminhamentosEspecializadosRecentes ?? []
      ),
    [canAcessoCBORegisterCid10, encaminhamentosCache?.encaminhamentosEspecializadosRecentes]
  )

  return (
    <Form<EncaminhamentoExternoEspecializadoModel>
      initialValues={
        {
          classificacaoRiscoEncaminhamento: ClassificacaoRiscoEncaminhamentoEnum.ELETIVO,
        } as EncaminhamentoExternoEspecializadoModel
      }
      render={renderForm}
      onSubmit={handleSubmit}
      validate={validator}
    />
  )
}

const classes = {
  container: css`
    margin-bottom: 1rem;
  `,
  formBody: css`
    padding-bottom: 1.25rem;
  `,
}
